import { useState } from "react";
import { Loader2, ListChecks, Check, GitBranch } from "lucide-react";
import adminApi from "../lib/adminApi";
import { Button } from "./ui/button";

// Dry-run first, apply only after the owner confirms: branches made before Your Ducks families existed get their
// original duck's id (and the original's flock). Running it again changes nothing. Never runs by itself.
export const FamilyBackfill = () => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const run = async (apply) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/ducks/family-backfill", { apply });
      setDone(apply ? data : null);
      setPreview(apply ? null : data);
    } catch {
      setError("The backfill could not run. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="family-backfill" className="mt-6 rounded-2xl border border-border bg-card/70 px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-sm flex-1 min-w-[220px]">
          <span className="font-semibold">Keep older branches with their original duck.</span>{" "}
          <span className="text-muted-foreground">Your Ducks groups a duck with its branches. Branches made before that need linking once; it shows what would change first.</span>
        </p>
        <Button data-testid="family-backfill-preview" size="sm" variant="outline" className="rounded-full h-9 gap-1.5" disabled={busy} onClick={() => run(false)}>
          {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />} Preview (dry run)
        </Button>
      </div>
      {preview && (
        <div data-testid="family-backfill-result" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs font-mono2 text-amber-900">
          <p data-testid="family-backfill-summary">
            {preview.would_update} of {preview.branches} branch{preview.branches === 1 ? "" : "es"} would be linked ({preview.families} famil{preview.families === 1 ? "y" : "ies"}, {preview.flock_changes} flock change{preview.flock_changes === 1 ? "" : "s"}) · {preview.already_done} already fine · {preview.orphans} without their original
          </p>
          <ul className="mt-1 space-y-0.5">
            {preview.examples.map((e) => (
              <li key={e.id} data-testid={`family-backfill-example-${e.id}`}>
                {e.name || e.id.slice(0, 8)} v{e.version} → family of {e.original_name || e.family_id.slice(0, 8)}{e.flock_from !== e.flock_to ? " (moves to the original's flock)" : ""}
              </li>
            ))}
          </ul>
          {preview.would_update > 0 && (
            <Button data-testid="family-backfill-apply" size="sm" className="mt-2 rounded-full h-9 gap-1.5 font-bold" disabled={busy} onClick={() => run(true)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply to {preview.would_update} branch{preview.would_update === 1 ? "" : "es"}
            </Button>
          )}
        </div>
      )}
      {done && (
        <p data-testid="family-backfill-done" className="mt-2 text-xs font-mono2 text-emerald-700">
          Applied: {done.updated} branch{done.updated === 1 ? "" : "es"} linked. Running it again changes nothing.
        </p>
      )}
      {error && <p data-testid="family-backfill-error" className="mt-2 text-xs text-rose-700">{error}</p>}
    </div>
  );
};
